import { Link, LinkProps } from 'react-router-dom';
import { useCallback, useState, useEffect, ReactNode } from 'react';

// Define route categories for smart prefetching
const LIGHT_ROUTES: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/pages/HomePage'),
  '/news': () => import('@/pages/NewsPage'),
  '/reviews': () => import('@/pages/VehicleReviewsPage'),
  '/events': () => import('@/pages/EventsPage'),
  '/featured': () => import('@/pages/FeaturedPage'),
  '/about': () => import('@/pages/AboutPage'),
  '/contact': () => import('@/pages/ContactPage'),
  '/dealers': () => import('@/pages/DealersPage'),
};

// Heavy routes - only prefetch on explicit interaction, not on mount
const HEAVY_ROUTES: Record<string, () => Promise<unknown>> = {
  '/deals/builder': () => import('@/pages/DealBuilderLandingPage'),
  '/deals/builder/create': () => import('@/pages/DealBuilderPage'),
  '/account': () => import('@/pages/BuyerAccountPage'),
  '/dealer/dashboard': () => import('@/pages/dealer/DealerDashboard'),
  '/dealer/account': () => import('@/pages/dealer/DealerAccountPage'),
  '/dealer/sales': () => import('@/pages/dealer/SalespersonDashboard'),
};

// All routes combined for lookup
const ALL_ROUTES = { ...LIGHT_ROUTES, ...HEAVY_ROUTES };

interface PrefetchLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  children: ReactNode;
  prefetchOnMount?: boolean; // Only for light critical links
  prefetchOnIdle?: boolean; // Prefetch when browser is idle
}

export function PrefetchLink({ 
  to, 
  children, 
  onMouseEnter, 
  prefetchOnMount = false,
  prefetchOnIdle = false,
  ...props 
}: PrefetchLinkProps) {
  const [prefetched, setPrefetched] = useState(false);

  // Check if this is a heavy route (dashboards, deal builder)
  const isHeavyRoute = to in HEAVY_ROUTES;
  
  // Prefetch on mount only for LIGHT routes
  useEffect(() => {
    if (prefetchOnMount && !prefetched && !isHeavyRoute && LIGHT_ROUTES[to]) {
      LIGHT_ROUTES[to]();
      setPrefetched(true);
    }
  }, [prefetchOnMount, prefetched, to, isHeavyRoute]);

  // Prefetch on idle for non-heavy routes
  useEffect(() => {
    if (prefetchOnIdle && !prefetched && !isHeavyRoute && ALL_ROUTES[to]) {
      if ('requestIdleCallback' in window) {
        const id = (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(() => {
          if (!prefetched && ALL_ROUTES[to]) {
            ALL_ROUTES[to]();
            setPrefetched(true);
          }
        });
        return () => {
          if ('cancelIdleCallback' in window) {
            (window as Window & typeof globalThis & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(id);
          }
        };
      }
    }
  }, [prefetchOnIdle, prefetched, to, isHeavyRoute]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!prefetched && ALL_ROUTES[to]) {
        // Prefetch on hover - works for all routes
        ALL_ROUTES[to]();
        setPrefetched(true);
      }
      onMouseEnter?.(e);
    },
    [to, prefetched, onMouseEnter]
  );

  // Touch start for mobile - only for light routes
  const handleTouchStart = useCallback(() => {
    if (!prefetched && ALL_ROUTES[to] && !isHeavyRoute) {
      ALL_ROUTES[to]();
      setPrefetched(true);
    }
  }, [to, prefetched, isHeavyRoute]);

  return (
    <Link 
      to={to} 
      onMouseEnter={handleMouseEnter} 
      onTouchStart={handleTouchStart}
      {...props}
    >
      {children}
    </Link>
  );
}

/**
 * Prefetch a route programmatically when browser is idle
 * Use this sparingly - only for routes the user is very likely to visit
 */
export function prefetchRouteOnIdle(path: string): void {
  if (ALL_ROUTES[path] && 'requestIdleCallback' in window) {
    (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
      ALL_ROUTES[path]();
    });
  }
}
